import { useMutation } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { FECHA_MINIMA } from "@/lib/fechas"

import type { Documento } from "./tipos"

/**
 * La portada del documento: versión, fechas, clasificación y las tres firmas.
 *
 * ⚠ **El tipo y el número NO se pueden cambiar.** Cambiarlos cambiaría el código de un
 * documento que otros ya citan. Cuando de verdad hace falta otro, se crea uno nuevo y
 * el viejo se marca `OBSOLETO`, que es lo que el SGI hace de verdad.
 *
 * ⚠ **Las firmas son texto, y no un selector de personas.** Se modelaron como
 * referencia a `personas` y los veinte documentos reales del SGI lo desmintieron:
 * quien firma es un ÓRGANO —«Junta de Socios», «Comité de Ética / Función de
 * Cumplimiento»—, no un individuo, y ninguno de esos cabe en un catálogo de personas
 * ni en el de cargos sin ensuciarlo.
 *
 * ⚠ **«Próxima revisión» tampoco es una fecha**, por lo mismo: lo que se escribe ahí
 * es una política —«Anual o ante cambio relevante»—. Con un selector de día habría
 * que inventarse uno.
 */

const FIRMAS = [
  ["elaborado_por", "Elaborado por"],
  ["revisado_por", "Revisado por"],
  ["aprobado_por", "Aprobado por"],
] as const

export function MetadatosDocumento({
  doc,
  onCerrar,
  onGuardado,
}: {
  doc: Documento
  onCerrar: () => void
  onGuardado: () => void
}) {
  const [campos, setCampos] = useState({
    titulo: doc.titulo,
    subtitulo: doc.subtitulo ?? "",
    clasificacion: doc.clasificacion ?? "",
    version: doc.version,
    fecha_emision: doc.fecha_emision ?? "",
    proxima_revision: doc.proxima_revision ?? "",
    relacionados: (doc.relacionados ?? []).join("\n"),
    elaborado_por: doc.elaborado_por ?? "",
    revisado_por: doc.revisado_por ?? "",
    aprobado_por: doc.aprobado_por ?? "",
  })

  const guardar = useMutation({
    mutationFn: () =>
      api.patch<Documento>(`/api/v1/biblioteca/documentos/${doc.id}`, {
        titulo: campos.titulo,
        subtitulo: campos.subtitulo || null,
        clasificacion: campos.clasificacion || null,
        version: campos.version,
        fecha_emision: campos.fecha_emision || null,
        proxima_revision: campos.proxima_revision || null,
        // Una norma por línea. Es texto libre a propósito: la mayoría de lo que se
        // cita es externo —«ISO 9001:2015, cláusula 7.5»— y no está en el sistema.
        relacionados: campos.relacionados
          .split("\n")
          .map((t) => t.trim())
          .filter(Boolean),
        elaborado_por: campos.elaborado_por || null,
        revisado_por: campos.revisado_por || null,
        aprobado_por: campos.aprobado_por || null,
      }),
    onSuccess: () => {
      onGuardado()
      onCerrar()
    },
  })

  const estilo = {
    borderColor: "var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--fg)",
  }

  return (
    <Modal abierto titulo="Portada del documento" onCerrar={onCerrar}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="m-titulo" className="mb-1 block text-xs font-medium uppercase">
            Título
          </label>
          <input
            id="m-titulo"
            value={campos.titulo}
            onChange={(e) => setCampos({ ...campos, titulo: e.target.value })}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="m-subtitulo" className="mb-1 block text-xs font-medium uppercase">
            Subtítulo
          </label>
          <input
            id="m-subtitulo"
            value={campos.subtitulo}
            onChange={(e) => setCampos({ ...campos, subtitulo: e.target.value })}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="m-version" className="mb-1 block text-xs font-medium uppercase">
            Versión
          </label>
          <input
            id="m-version"
            value={campos.version}
            onChange={(e) => setCampos({ ...campos, version: e.target.value })}
            maxLength={20}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="m-clasificacion" className="mb-1 block text-xs font-medium uppercase">
            Clasificación
          </label>
          <input
            id="m-clasificacion"
            value={campos.clasificacion}
            onChange={(e) => setCampos({ ...campos, clasificacion: e.target.value })}
            placeholder="Uso interno"
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="m-emision" className="mb-1 block text-xs font-medium uppercase">
            Fecha de emisión
          </label>
          <input
            id="m-emision"
            type="date"
            min={FECHA_MINIMA}
            value={campos.fecha_emision}
            onChange={(e) => setCampos({ ...campos, fecha_emision: e.target.value })}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="m-revision" className="mb-1 block text-xs font-medium uppercase">
            Próxima revisión
          </label>
          <input
            id="m-revision"
            value={campos.proxima_revision}
            onChange={(e) => setCampos({ ...campos, proxima_revision: e.target.value })}
            placeholder="Anual o ante cambio relevante"
            maxLength={160}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          />
        </div>

        {FIRMAS.map(([campo, etiqueta]) => (
          <div key={campo}>
            <label htmlFor={`m-${campo}`} className="mb-1 block text-xs font-medium uppercase">
              {etiqueta}
            </label>
            <input
              id={`m-${campo}`}
              value={campos[campo]}
              onChange={(e) => setCampos({ ...campos, [campo]: e.target.value })}
              placeholder="Junta de Socios"
              maxLength={200}
              className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
              style={estilo}
            />
          </div>
        ))}

        <div className="sm:col-span-2">
          <label htmlFor="m-relacionados" className="mb-1 block text-xs font-medium uppercase">
            Documentos y normas relacionados
          </label>
          <textarea
            id="m-relacionados"
            value={campos.relacionados}
            onChange={(e) => setCampos({ ...campos, relacionados: e.target.value })}
            rows={3}
            placeholder={"ISO 9001:2015, cláusula 7.5\nLey Orgánica de Aduanas"}
            className="w-full rounded-[var(--radius-control)] border px-2 py-1.5 text-sm"
            style={estilo}
          />
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            Uno por línea.
          </p>
        </div>
      </div>

      {guardar.isError && (
        <p role="alert" className="mt-4 text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton variante="primario" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
      </div>
    </Modal>
  )
}
